import React, { useEffect, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
import NavigationService from '../../../../utils/NavigationService'
import URLTrigger from '../../../../URLSystem/URLTrigger'
import { clearAppModelContext, setAppModelContext } from '../../../../utils/annaAppModelContext'
import { EMBED_LOCATION_MESSAGE_TYPE, resolveReportedLocation } from './embeddedAppLocation'
import { TEAM_ORIGIN } from './embeddedToolHandoff'

// Required lazily: the frame is also hosted by the assistant shell (AnnaShell), and a static import
// of the roadmap bridge and the Firebase client would pull the Firestore mapping chain into
// everything that imports the shell.
const roadmapBridge = () => require('../../../../utils/roadmapSourceBridge')
const runHttpsCallableFunction = (...args) =>
    require('../../../../utils/backends/firestore').runHttpsCallableFunction(...args)

// The only messages an embedded app speaks. The window `message` event is a shared
// bus, not a private channel: react-native-web's scheduler runs on the
// `setimmediate` polyfill, which implements setImmediate as
// `window.postMessage('setImmediate$<rand>$<handle>', '*')` on OUR OWN window —
// so every scheduled tick delivers a same-origin string here. Treating those as
// "a message from the iframe that failed the origin check" logged a warning per
// tick (hundreds while a modal is open) and buried a real cross-origin attempt
// in the noise. Anything not shaped like our protocol is dropped silently; the
// origin check below still guards every message that IS.
const ROADMAP_MESSAGE_TYPES = new Set([
    'ROADMAP_PROJECTS_REQUEST',
    'ROADMAP_PROJECT_SUBSCRIBE',
    'ROADMAP_PROJECT_UNSUBSCRIBE',
    'ROADMAP_OPEN_ENTITY',
])
const IFRAME_MESSAGE_TYPES = new Set([
    'GET_USER_DATA',
    'GET_ALLDONE_TOKEN',
    'ALLDONE_SERVER_BILLING',
    'DEDUCT_GOLD',
    'REFUND_GOLD',
    'APP_CONTEXT',
    EMBED_LOCATION_MESSAGE_TYPE,
    'EMBED_HANDOFF_READY',
    'EMBED_HANDOFF_CAPTURED',
    'EMBED_HANDOFF_RESTORE_REQUEST',
    'EMBED_HANDOFF_RESTORED',
    ...ROADMAP_MESSAGE_TYPES,
])
// alldone.team pages exchange this token for a session as the person's linked account and are then
// billed per action on the server (functions/index.js getAlldoneTeamEmbedTokenSecondGen).
const getAlldoneTeamEmbedToken = async () => {
    try {
        const result = await runHttpsCallableFunction('getAlldoneTeamEmbedTokenSecondGen', {})
        return result?.token || null
    } catch (error) {
        console.warn('IframeModal: no alldone.team token', error?.message)
        return null
    }
}

const isRoadmapUrl = url => {
    try {
        const path = new URL(url).pathname
        return path === '/embed/create-roadmap' || path.includes('/paul-product-manager/create-roadmap')
    } catch (error) {
        return false
    }
}

/**
 * An embedded web app (a pre-configured iframe task, e.g. an alldone.team tool page) together with
 * the postMessage protocol it speaks with Alldone: user data, the alldone.team token, Gold and the
 * roadmap source. Hosted by the app-wide embed window (IframeModal) and by the assistant shell's
 * right pane (AnnaShell, AT-2951), so an app behaves the same in both places.
 *
 * `onNavigateAway` runs before the app opens an Alldone object (ROADMAP_OPEN_ENTITY), so its host
 * can step aside and show the Alldone workspace.
 *
 * `contextId` (the assistant pane's view id, AT-2955) lets the app tell the assistant what it shows:
 * `{ type: 'APP_CONTEXT', context: { text?, structuredContent? } }`, the iframe counterpart of MCP
 * Apps' `ui/update-model-context`. Accepted only from this frame on its own origin, kept as
 * untrusted app-reported state (utils/annaAppModelContext.js) and never logged. Without a
 * `contextId` (the embed window) it is ignored: nobody would read it.
 *
 * `onLocationChange(url)` receives the page the app reports with EMBED_LOCATION (AT-2954), already
 * checked to be on the frame's own origin. A cross-origin frame cannot be observed otherwise, so an
 * app that never reports leaves its host with the start URL.
 */
export default function EmbeddedAppFrame({
    url,
    title,
    onNavigateAway,
    onLocationChange,
    onHandoffReady,
    handoffRestore,
    suspended = false,
    contextId = null,
}) {
    const loggedUser = useSelector(state => state.loggedUser)
    const loggedUserProjects = useSelector(state => state.loggedUserProjects)
    const iframeRef = useRef(null)

    const finalUrl = url
    const roadmapProjects = useMemo(
        () => roadmapBridge().getActiveRoadmapProjects(loggedUserProjects, loggedUser),
        [loggedUserProjects, loggedUser]
    )
    const loggedUserRef = useRef(loggedUser)
    const roadmapProjectsRef = useRef(roadmapProjects)
    loggedUserRef.current = loggedUser
    roadmapProjectsRef.current = roadmapProjects
    // Read through a ref so a new callback each render does not rebuild the bridge mid-session.
    const onNavigateAwayRef = useRef(onNavigateAway)
    onNavigateAwayRef.current = onNavigateAway
    const onLocationChangeRef = useRef(onLocationChange)
    onLocationChangeRef.current = onLocationChange
    const onHandoffReadyRef = useRef(onHandoffReady)
    onHandoffReadyRef.current = onHandoffReady
    const handoffRestoreRef = useRef(handoffRestore)
    handoffRestoreRef.current = handoffRestore

    useEffect(() => {
        if (!finalUrl) return

        let trustedOrigin = null
        let unsubscribeRoadmapProject = null
        let pendingCapture = null
        let restored = false
        // Set once an alldone.team page has signed in as the person: its server bills every action in
        // Gold, so the page's own DEDUCT_GOLD / REFUND_GOLD are acknowledged here, never charged twice.
        let serverBilled = false

        try {
            trustedOrigin = finalUrl ? new URL(finalUrl).origin : null
        } catch (error) {
            console.error('IframeModal: invalid iframe URL', {
                url: finalUrl,
                error: error.message,
            })
        }

        const handleMessage = async event => {
            // Not addressed to us (our own scheduler, an SDK, a browser
            // extension): ignore without a word.
            if (event.source === window) return

            const messageType = event?.data?.type
            if (typeof messageType !== 'string' || !IFRAME_MESSAGE_TYPES.has(messageType)) return

            // A trusted origin can host many tools. Require both the exact iframe
            // window and the roadmap capability URL before exposing project data.
            if (event.source !== iframeRef.current?.contentWindow) return

            if (!trustedOrigin || event.origin !== trustedOrigin) {
                console.warn('IframeModal: ignoring message from untrusted origin', {
                    origin: event.origin,
                    trustedOrigin,
                    type: messageType,
                })
                return
            }

            const { type, amount } = event.data

            if (type.startsWith('EMBED_HANDOFF_')) {
                if (event.origin !== TEAM_ORIGIN) return
                const { id } = event.data
                const restore = handoffRestoreRef.current
                if (type === 'EMBED_HANDOFF_READY' && !restore) {
                    onHandoffReadyRef.current?.(
                        captureId =>
                            new Promise(resolve => {
                                pendingCapture?.resolve(null)
                                pendingCapture = { id: captureId, resolve }
                                event.source.postMessage({ type: 'EMBED_HANDOFF_CAPTURE', id: captureId }, event.origin)
                            })
                    )
                } else if (type === 'EMBED_HANDOFF_CAPTURED' && id === pendingCapture?.id) {
                    const { resolve } = pendingCapture
                    pendingCapture = null
                    resolve(event.data)
                } else if (restore && id === restore.id && !restored) {
                    if (type === 'EMBED_HANDOFF_RESTORE_REQUEST') {
                        event.source.postMessage({ type: 'EMBED_HANDOFF_RESTORE', id }, event.origin)
                    } else if (type === 'EMBED_HANDOFF_RESTORED') {
                        restored = true
                        restore.onRestored(event.data.success === true)
                    }
                }
                return
            }

            if (type === 'APP_CONTEXT') {
                if (contextId) setAppModelContext(contextId, event.data.context)
                return
            }

            // Sent on every navigation inside the app: handled before the protocol log, which would
            // otherwise print a line (and the page's address) per navigation.
            if (type === EMBED_LOCATION_MESSAGE_TYPE) {
                const location = resolveReportedLocation(event.data.url, finalUrl)
                if (location) onLocationChangeRef.current?.(location)
                return
            }

            if (ROADMAP_MESSAGE_TYPES.has(type)) {
                if (
                    !isRoadmapUrl(finalUrl) ||
                    event.data.protocolVersion !== roadmapBridge().ALLDONE_ROADMAP_PROTOCOL_VERSION
                )
                    return
            }

            console.log('IframeModal: message received from iframe', {
                origin: event.origin,
                type,
                amount,
                userEmail: loggedUserRef.current?.email || '',
            })

            const postResult = message => {
                console.log('IframeModal: posting message to iframe', {
                    origin: event.origin,
                    messageType: message?.type,
                    newBalance: message?.newBalance,
                    error: message?.error,
                })
                event.source.postMessage(message, event.origin)
            }

            const handleGoldRequest = async ({ callableName, successType, errorType, errorLogLabel, source }) => {
                if (!Number.isFinite(amount) || amount <= 0) {
                    postResult({
                        type: errorType,
                        error: 'Invalid gold amount',
                    })
                    return
                }

                try {
                    console.log('IframeModal: calling gold function', {
                        callableName,
                        amount,
                        userEmail: loggedUserRef.current?.email || '',
                    })

                    // Routed through the offline-aware funnel (AT-2340): a gold
                    // change is server-authoritative, so offline this now fails
                    // immediately and the embedded surface gets a clean error
                    // instead of a ~70s hang. The funnel unwraps the envelope.
                    const result = await runHttpsCallableFunction(callableName, {
                        gold: amount,
                        source,
                        channel: 'iframe',
                    })

                    console.log('IframeModal: gold function responded', {
                        callableName,
                        amount,
                        result,
                    })

                    if (result.success) {
                        postResult({
                            type: successType,
                            newBalance: result.newBalance,
                        })
                    } else {
                        postResult({
                            type: errorType,
                            error: result.message,
                        })
                    }
                } catch (error) {
                    console.error(errorLogLabel, error)
                    postResult({
                        type: errorType,
                        error: error.message,
                    })
                }
            }

            if (type === 'GET_USER_DATA') {
                postResult({
                    type: 'USER_DATA',
                    user: {
                        email: loggedUserRef.current?.email,
                        name: loggedUserRef.current?.userName || loggedUserRef.current?.name,
                        gold: loggedUserRef.current?.gold || 0,
                    },
                    alldoneToken: event.origin === TEAM_ORIGIN ? await getAlldoneTeamEmbedToken() : null,
                })
            }

            if (type === 'GET_ALLDONE_TOKEN' && event.origin === TEAM_ORIGIN) {
                postResult({ type: 'ALLDONE_TOKEN', alldoneToken: await getAlldoneTeamEmbedToken() })
            }

            if (type === 'ALLDONE_SERVER_BILLING' && event.origin === TEAM_ORIGIN) {
                serverBilled = true
                return
            }

            if (serverBilled && (type === 'DEDUCT_GOLD' || type === 'REFUND_GOLD')) {
                postResult({
                    type: type === 'DEDUCT_GOLD' ? 'DEDUCT_GOLD_SUCCESS' : 'REFUND_GOLD_SUCCESS',
                    newBalance: loggedUserRef.current?.gold || 0,
                })
                return
            }

            if (type === 'DEDUCT_GOLD') {
                console.log('IframeModal: processing DEDUCT_GOLD request', { amount })
                await handleGoldRequest({
                    callableName: 'deductGoldSecondGen',
                    successType: 'DEDUCT_GOLD_SUCCESS',
                    errorType: 'DEDUCT_GOLD_ERROR',
                    errorLogLabel: 'Error deducting gold:',
                    source: 'iframe_deduction',
                })
            }

            if (type === 'REFUND_GOLD') {
                console.log('IframeModal: processing REFUND_GOLD request', { amount })
                await handleGoldRequest({
                    callableName: 'refundGoldSecondGen',
                    successType: 'REFUND_GOLD_SUCCESS',
                    errorType: 'REFUND_GOLD_ERROR',
                    errorLogLabel: 'Error refunding gold:',
                    source: 'iframe_refund',
                })
            }

            if (type === 'ROADMAP_PROJECTS_REQUEST') {
                postResult({
                    type: 'ROADMAP_PROJECTS',
                    protocolVersion: roadmapBridge().ALLDONE_ROADMAP_PROTOCOL_VERSION,
                    projects: roadmapProjectsRef.current,
                })
            }

            if (type === 'ROADMAP_PROJECT_SUBSCRIBE') {
                const project = roadmapProjectsRef.current.find(candidate => candidate.id === event.data.projectId)
                if (!project || !loggedUserRef.current?.uid) {
                    postResult({
                        type: 'ROADMAP_PROJECT_ERROR',
                        protocolVersion: roadmapBridge().ALLDONE_ROADMAP_PROTOCOL_VERSION,
                        projectId: event.data.projectId,
                        error: 'This project is not available.',
                    })
                    return
                }

                unsubscribeRoadmapProject?.()
                unsubscribeRoadmapProject = roadmapBridge().subscribeToRoadmapProject({
                    projectId: project.id,
                    userId: loggedUserRef.current.uid,
                    onSnapshot: snapshot =>
                        postResult({
                            type: 'ROADMAP_PROJECT_SNAPSHOT',
                            protocolVersion: roadmapBridge().ALLDONE_ROADMAP_PROTOCOL_VERSION,
                            snapshot,
                        }),
                    onError: error =>
                        postResult({
                            type: 'ROADMAP_PROJECT_ERROR',
                            protocolVersion: roadmapBridge().ALLDONE_ROADMAP_PROTOCOL_VERSION,
                            projectId: project.id,
                            error: error?.message || 'Could not read this project.',
                        }),
                })
            }

            if (type === 'ROADMAP_PROJECT_UNSUBSCRIBE') {
                unsubscribeRoadmapProject?.()
                unsubscribeRoadmapProject = null
            }

            if (type === 'ROADMAP_OPEN_ENTITY') {
                const hasProjectAccess = roadmapProjectsRef.current.some(project => project.id === event.data.projectId)
                if (!hasProjectAccess) return
                const path = roadmapBridge().getRoadmapNavigationPath({
                    projectId: event.data.projectId,
                    userId: loggedUserRef.current?.uid,
                    entityType: event.data.entityType,
                    entityId: event.data.entityId,
                })
                if (!path) return
                onNavigateAwayRef.current?.()
                URLTrigger.processUrl(NavigationService, path)
            }
        }

        window.addEventListener('message', handleMessage)
        return () => {
            window.removeEventListener('message', handleMessage)
            pendingCapture?.resolve(null)
            onHandoffReadyRef.current?.(null)
            unsubscribeRoadmapProject?.()
            if (contextId) clearAppModelContext(contextId)
        }
    }, [finalUrl, contextId])

    // Push active-project list changes as well as answering explicit requests.
    // This covers the iframe opening before the app's project watcher has delivered.
    useEffect(() => {
        if (!isRoadmapUrl(finalUrl) || !iframeRef.current?.contentWindow) return
        let targetOrigin
        try {
            targetOrigin = new URL(finalUrl).origin
        } catch (error) {
            return
        }
        iframeRef.current.contentWindow.postMessage(
            {
                type: 'ROADMAP_PROJECTS',
                protocolVersion: roadmapBridge().ALLDONE_ROADMAP_PROTOCOL_VERSION,
                projects: roadmapProjects,
            },
            targetOrigin
        )
    }, [finalUrl, roadmapProjects])

    return (
        <iframe
            ref={iframeRef}
            src={finalUrl}
            inert={suspended ? '' : undefined}
            style={{
                width: '100%',
                height: '100%',
                flex: 1,
                border: 'none',
            }}
            allow="display-capture; microphone; camera; autoplay; clipboard-read; clipboard-write"
            allowFullScreen
            title={title || 'Task Iframe'}
        />
    )
}
